import React, { ReactNode, useMemo, useState } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import type { TFunction } from 'i18next';
import {
  ApproveSprintFormValues,
  DisputeSprintFormValues,
  SprintEntity,
  SprintProductStatus,
  useApproveSprintProduct,
  useClientProductTime,
  useDisputeSprintProduct,
  useListSprintProducts,
  useSprintProductsForViewer,
} from '@data';
import { Button } from '@ui/components/ui/button';
import { clientReportingPath, productReportingPath } from '@app/constants/url';
import { isProductIncidentsEnabled } from '@app/constants/featureFlags';
import { useProductIncidents } from '@data/hooks/incidents';
import { Loader } from '@ui';
import { useOrganizationId } from '@app/utils/hooks';
import { useOrganizationChapters } from '@data/hooks/organization';
import { CircleCheck, TriangleAlert } from 'lucide-react';
import {
  useProductCrashFree,
  useProductDependencies,
  useProductResponseTime,
  useProductSecurity,
} from '@data/hooks/products';
import { ApprovalPopup, Card, DisputePopup, SectionHeading, StatCard, hasSettledRemark } from '@app/components/core';
import { reportTileSpan } from './overviewLayout';
import { hasSeenDisputeAnswer } from '../sprintProduct/disputeAnswerSeen';
import { ArchivedNote } from './ArchivedNote';
import CardArrow from './CardArrow';
import { DependenciesCaption, DependencyList } from './DependencyList';
import SecurityIssues from './SecurityIssues';
import TabArrow from '../../products/productDetail/TabArrow';
import { CrashFreeTrendChart, ResponseSparkline, ResponseTrendChart, StackedBarChart } from './charts';
import { aikido } from './sources/aikido';
import { github } from './sources/github';
import { sentry } from './sources/sentry';
import { bySlot, chapterColor, chapterSlots } from './sources/chapters';
import { useIsInternalReader } from '../internalReader';
import { TimeReport, buildTimeReport, monthLabels, sprintRangeLabel, timeReportWindow } from './sources/timeReport';
import { pendingRead, sprintBudget, sprintUnpriced, unpriced } from './spendFigures';
import { ringArcs } from './dependencyRing';
import { ACTIVE_HOURS, type YearSpend } from '../../products/productDetail/yearSpend';
import {
  countStatus,
  dependenciesThresholdShares,
  RESPONSE_FAST_BELOW_MS,
  RESPONSE_SLOW_AT_OR_ABOVE_MS,
  THRESHOLDS,
} from './sources/thresholds';
import {
  CrashFreeMetric,
  DependenciesMetric,
  HealthStatus,
  ResponseTimeMetric,
  SecurityMetric,
  STATUS_META,
} from './sources/types';

// Compact millisecond label, e.g. 380 → "380ms", 1500 → "1.5s". Used by the chart's Y axis and by the
// response-time threshold line, which is why it stands above both.
const formatMsTick = (ms: number): string => (ms >= 1000 ? `${+(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`);

/**
 * The line each card states under its title, so amber is a number the client can read rather than a
 * colour somebody chose.
 *
 * Every one of these interpolates the same constant the status function applies, so the sentence cannot
 * drift from the rule. Change a threshold and the card says the new one.
 */
const securityThreshold = (t: TFunction): string => t('productDashboard.securityThreshold');

// Every rule the card is judged by, and what unsupported means, since a single unsupported package earns amber.
const dependenciesThreshold = (t: TFunction): string =>
  t('productDashboard.dependenciesThreshold', dependenciesThresholdShares());

const responseTimeThreshold = (t: TFunction): string =>
  t('productDashboard.responseTimeThreshold', {
    fast: formatMsTick(RESPONSE_FAST_BELOW_MS),
    slow: formatMsTick(RESPONSE_SLOW_AT_OR_ABOVE_MS),
  });

// Plain white card; colour comes only from the objective numbers/charts inside, never the container.
const HealthCard = ({
  title,
  threshold,
  aside,
  children,
  className,
}: {
  title: React.ReactNode;
  threshold?: string;
  aside?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}): JSX.Element => (
  <Card className={cx('p-6', className)}>
    <div className="mb-4 flex flex-wrap items-center gap-x-8 gap-y-2">
      <div>
        <p className="mb-1 text-base font-semibold text-foreground">{title}</p>
        {/* What earns amber, stated rather than left to the colour. It sits above the reading and outside
            every no-data branch on purpose: a client looking at a card with nothing in it should still be
            able to see what will be measured when there is. */}
        {threshold && <p className="text-xs text-muted-foreground">{threshold}</p>}
      </div>
      {aside && <div className="sm:border-l sm:border-border sm:pl-8">{aside}</div>}
    </div>
    {children}
  </Card>
);

const wholeEuros = new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const euroTick = (value: number): string =>
  value >= 1000 ? `€${+(value / 1000).toFixed(1)}k` : `€${Math.round(value)}`;

// The delivery figures come from Phill's own data — hours reported on the product and the sprints
// delivered to the client — so they need no source outside the product. The prototype leads these with a
// project duration read from a snapshot fixture; that has no source, so it does not ship.
const progressStats = (
  t: TFunction,
  delivery: { hoursLastSprint: number; sprintsDelivered: number },
): { label: string; value: string; sub: string }[] => [
  // Recent effort, then the delivery count (hours alone can't show how much has shipped). Both
  // repeat what the product card shows, so the two views agree.
  {
    label: t('productDashboard.hoursLastSprint'),
    value: t('productDashboard.chapterHours', { count: Math.round(delivery.hoursLastSprint) }),
    sub: t('productDashboard.hoursLastSprintSub'),
  },
  {
    label: t('productDashboard.sprintsDelivered'),
    value: String(delivery.sprintsDelivered),
    sub: t('productDashboard.sprintsSubToDate'),
  },
];

// A per-sprint stacked-bar report split by chapter. The legend on the right doubles as a chapter
// selector — toggle a chapter to drop it from every bar. Colour is reserved for chapter identity here.
// The prototype pairs this with a Budget view of the same bars; that is the financial surface this epic
// does not ship, so the segmented control goes with it and the chart is hours only.
const DeliveryReportCard = ({
  report,
  title,
  caption,
  format,
  formatTick,
  action,
}: {
  report: Pick<TimeReport, 'chapters' | 'periodLabels' | 'periodTips'>;
  title: string;
  /** A control in the header's corner, e.g. the arrow into Spend. */
  action?: ReactNode;
  caption: string;
  format: (value: number) => string;
  formatTick: (value: number) => string;
}): JSX.Element => {
  const { t } = useTranslation();
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());
  const toggle = (key: string): void =>
    setHidden(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const { data: chapterData } = useOrganizationChapters(useOrganizationId(), { includeDisabled: true });
  const slots = useMemo(() => chapterSlots(chapterData?.items ?? []), [chapterData]);
  const chapters = [...report.chapters].sort(bySlot(slots));

  // Bottom → top stack order follows the slot order; each series carries its fixed chapter colour. All
  // series are passed to the chart (not pre-filtered) so hiding one animates rather than pops.
  const series = chapters.map(chapter => ({
    key: chapter.key,
    label: chapter.label,
    color: chapterColor(slots.get(chapter.key)),
    data: chapter.values,
  }));

  return (
    <Card className="flex flex-col p-6">
      <div className="flex items-start justify-between gap-3">
        <p className="text-base font-semibold text-foreground">{title}</p>
        {action}
      </div>
      <div className="mt-4 flex flex-col gap-4">
        <div className="flex min-w-0 items-center">
          <StackedBarChart
            series={series}
            labels={report.periodLabels}
            hiddenKeys={hidden}
            tip={(seriesIndex, barIndex, value) =>
              `${report.periodTips[barIndex]} · ${series[seriesIndex].label}: ${format(value)}`
            }
            formatTick={formatTick}
            heightClass="h-40"
            className="w-full"
          />
        </div>

        {/* Chapter selector under the chart, one per chapter in the sprints, so the bars get the full width. */}
        <div>
          <ul className="flex flex-wrap gap-x-2 gap-y-1" aria-label={t('productDashboard.chapters')}>
            {chapters.map(chapter => {
              const isHidden = hidden.has(chapter.key);
              const total = chapter.values.reduce((sum, v) => sum + v, 0);
              const color = chapterColor(slots.get(chapter.key));
              return (
                <li key={chapter.key}>
                  <button
                    type="button"
                    onClick={() => toggle(chapter.key)}
                    aria-pressed={!isHidden}
                    className={cx(
                      'flex items-center gap-2 rounded-md px-2 py-1 text-left transition duration-200 hover:bg-muted',
                      isHidden && 'opacity-45',
                    )}
                  >
                    {/* Filled swatch when shown; hollow (ring only) when hidden — never colour alone. */}
                    <span
                      aria-hidden
                      className="size-3 shrink-0 rounded-[3px] transition-all duration-200"
                      style={{
                        backgroundColor: isHidden ? 'transparent' : color,
                        boxShadow: `inset 0 0 0 1.5px ${color}`,
                      }}
                    />
                    <span className="text-sm text-foreground">{chapter.label}</span>
                    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{format(total)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      <p className="mt-4 text-xs text-muted-foreground">{caption}</p>
    </Card>
  );
};

// The last year of reported time for this product. Both the stat tiles and the chart below them read
// it; the second caller is served from React Query's cache, so it stays one request.
const useProductTimeReport = (): { report: TimeReport; isLoading: boolean } => {
  const organizationId = useOrganizationId();
  const { productId } = useParams<{ productId: string }>();
  const window = useMemo(() => timeReportWindow(), []);
  const { data, isLoading } = useClientProductTime(organizationId, productId, {
    ...window,
    asClient: useIsInternalReader(),
  });
  return { report: buildTimeReport(data?.items ?? []), isLoading };
};

// Distinct sprints delivered to the client — the same states and the same count the product card
// shows, so a client reads one number for "how much has shipped" in both places.
const DELIVERED_STATUSES = [SprintProductStatus.closed, SprintProductStatus.approved, SprintProductStatus.invoiced];

const useSprintsDelivered = (): number => {
  const organizationId = useOrganizationId();
  const { productId } = useParams<{ productId: string }>();
  const { data } = useSprintProductsForViewer(useIsInternalReader(), organizationId, {
    statuses: DELIVERED_STATUSES,
    productId,
  });
  return new Set(
    (data?.items ?? [])
      .filter(item => item.product?.id === productId)
      .map(item => item.sprint?.id)
      .filter(Boolean),
  ).size;
};

// Renders the last year of reported time as the delivery report.
const DeliveryReport = (): JSX.Element => {
  const { t } = useTranslation();
  const { report, isLoading } = useProductTimeReport();

  if (isLoading) return <Loader />;
  // No sprints in the window at all — there's no axis to draw, let alone bars.
  if (!report.periodLabels.length) {
    return (
      <Card className="p-6">
        <p className="py-8 text-center text-sm text-muted-foreground">{t('productDashboard.noReportedTime')}</p>
      </Card>
    );
  }
  return (
    <DeliveryReportCard
      report={report}
      title={t('productDashboard.timeSpent')}
      caption={t('productDashboard.timeSpentCaption')}
      format={value => t('productDashboard.chapterHours', { count: Math.round(value) })}
      formatTick={value => `${Math.round(value)}h`}
    />
  );
};

// The year so far and the latest sprint that cost anything: the chart's total, and its last bar.
/** One sprint's bill: its chapters' spend and any fixed price, less what Costs took off it, retention included. */
const sprintCost = (spend: YearSpend, sprintId: string, index: number): number =>
  spend.series.reduce((sum, each) => sum + (each.values[index] ?? 0), 0) +
  (spend.fixedBySprint?.[sprintId] ?? 0) -
  (spend.discountBySprint[sprintId] ?? 0) -
  (spend.retentionBySprint[sprintId] ?? 0);

const spendStats = (t: TFunction, spend: YearSpend): { label: string; value: string; sub: string }[] => {
  const pending = unpriced(t, spend);
  const failedSub = spend.failed ? t('cardReadFailed') : null;
  // Only the sprints that cost something: one nobody worked yet would pull the average down.
  const priced = spend.sprints.map((sprint, index) => sprintCost(spend, sprint.id, index)).filter(amount => amount > 0);
  return [
    {
      label: t('productDashboard.spentThisYear', { year: spend.year }),
      value: pending ?? wholeEuros.format(spend.total),
      sub: failedSub ?? t('productDashboard.spentThisYearSub'),
    },
    {
      label: t('productDashboard.averageSprint', { year: spend.year }),
      value: pending ?? wholeEuros.format(priced.length ? spend.total / priced.length : 0),
      sub: failedSub ?? t('productDashboard.averageSprintSub', { count: priced.length }),
    },
  ];
};

// The same bars in money: this year's sprints so far, each priced the way the Spend tab prices it.
const SpendReport = ({ spend }: { spend: YearSpend }): JSX.Element => {
  const { t } = useTranslation();
  if (spend.isLoading) return <Loader />;
  if (spend.failed || !spend.series.length) {
    return (
      <Card className="p-6">
        <p className="py-8 text-center text-sm text-muted-foreground">
          {spend.failed ? t('cardReadFailed') : t('productDashboard.noSpend', { year: spend.year })}
        </p>
      </Card>
    );
  }
  const starts = spend.sprints.map(sprint => new Date(sprint.startDate));
  const report = {
    chapters: spend.series,
    periodLabels: monthLabels(starts),
    periodTips: spend.sprints.map((sprint, index) =>
      t('productDashboard.sprintTip', {
        number: sprint.number,
        range: sprintRangeLabel(starts[index], new Date(sprint.endDate)),
      }),
    ),
  };
  return (
    <DeliveryReportCard
      report={report}
      title={t('productDashboard.spendTitle')}
      caption={t('productDashboard.spendCaption', { year: spend.year })}
      format={value => wholeEuros.format(value)}
      formatTick={euroTick}
      action={<TabArrow tab="costs" scrollTo="spend-workforce" />}
    />
  );
};

// One cohesive delivery section: cumulative + recency stats, then the per-sprint delivery report
// stacked by chapter with a toggle-able legend.
const HoursProgress = ({ actions }: { actions?: ReactNode }): JSX.Element => {
  const { t } = useTranslation();
  // The most recent sprint that actually carries reported time — not the last bucket in the window, which
  // is a sprint the team hasn't worked (or closed) yet and would read as 0h.
  const { report } = useProductTimeReport();
  const sprintsDelivered = useSprintsDelivered();
  const delivery = { hoursLastSprint: report.hoursSeries.findLast(hours => hours > 0) ?? 0, sprintsDelivered };
  return (
    <section>
      <SectionHeading action={actions}>{t('productDashboard.progressSection')}</SectionHeading>

      <div className="mt-2 mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        {progressStats(t, delivery).map(stat => (
          <StatCard key={stat.label} stat={stat} />
        ))}
      </div>

      <DeliveryReport />
    </section>
  );
};

type Tile = { label: string; value: string; sub: string };

type LatestReport = { sprint: SprintEntity; state: string; summary: string | null; answerUnread: boolean };

// The latest report the client has been sent, and where it stands with them.
const useLatestReport = (): LatestReport | null => {
  const organizationId = useOrganizationId();
  const { productId } = useParams<{ productId: string }>();
  const { data } = useSprintProductsForViewer(useIsInternalReader(), organizationId, {
    statuses: DELIVERED_STATUSES,
    productId,
  });
  const latest = (data?.items ?? [])
    .filter(item => item.product?.id === productId && item.sprint)
    .sort((a, b) => (b.sprint?.startDate ?? '').localeCompare(a.sprint?.startDate ?? ''))[0];
  if (!latest?.sprint) return null;
  const disputed = !!latest.disputedAt && !latest.disputeResolvedAt;
  // The sprint page's own test: an answer to their remark they have not read holds approval back.
  const answerUnread =
    latest.status === SprintProductStatus.closed &&
    hasSettledRemark(latest) &&
    !!latest.disputeResolution &&
    !hasSeenDisputeAnswer(latest.id, latest.disputeResolvedAt);
  return {
    sprint: latest.sprint,
    state: disputed ? 'disputed' : latest.status,
    answerUnread,
    summary: latest.summary?.trim() || null,
  };
};

/** The report's standing as a chip: amber while it waits on the client, red on an open remark. */
const REPORT_STATE_CHIP: Record<string, string> = {
  closed: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300',
  approved: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300',
  invoiced: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300',
  disputed: 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300',
  answered: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300',
};

// The last report in brief: what it cost, how much work went in, where the money went, and what it said.
const LatestReportTile = ({
  report,
  spend,
  className,
}: {
  report: LatestReport;
  spend: YearSpend;
  className?: string;
}): JSX.Element => {
  const { t } = useTranslation();
  const organizationId = useOrganizationId();
  const { productId = '' } = useParams<{ productId: string }>();
  const internal = useIsInternalReader();
  const { sprint } = report;
  const index = spend.sprints.findIndex(each => each.id === sprint.id);
  // Only a sprint of the year on show is priced here; an older one is still read, just without figures.
  const cost = index < 0 ? 0 : sprintCost(spend, sprint.id, index);
  // Unpriced hours stay a dash even where a release or a fixed charge moved the sprint's figure.
  const costUnpriced = index >= 0 && sprintUnpriced(spend, sprint.id, index);
  // A failed read leaves the figures out rather than printing zero hours at zero cost.
  const work = spend.failed ? undefined : spend.bySprint[sprint.id];
  // Hidden with the cost where the hours had no rate, so no €0 budget or over/under line stands beside a dash.
  const budgeted = index < 0 ? (spend.budgetBySprint[sprint.id] ?? null) : sprintBudget(spend, sprint.id, index);
  const to = internal
    ? productReportingPath(organizationId ?? '', productId, sprint.id)
    : clientReportingPath(organizationId, productId, sprint.id);

  // The report is answered here, by the client or by us: a PM records the answer a client gave by other means.
  // Not while our answer to their remark is unread: the report shows it first, as the sprint page does.
  const holdsForAnswer = !internal && report.answerUnread;
  const awaiting = report.state === SprintProductStatus.closed && !holdsForAnswer;
  const chip = holdsForAnswer ? 'answered' : report.state;
  const [deciding, setDeciding] = useState<'approve' | 'dispute' | null>(null);
  const { mutate: approve, isPending: isApproving } = useApproveSprintProduct();
  const { mutate: dispute, isPending: isDisputing } = useDisputeSprintProduct();
  const { data: sprintProduct, isLoading: sprintProductLoading } = useListSprintProducts({
    sprintId: sprint.id,
    productId: deciding ? productId : undefined,
  });
  const done = { onSuccess: () => setDeciding(null) };
  const which = t('productDashboard.latestReportWhich', {
    number: sprint.number,
    range: sprintRangeLabel(new Date(sprint.startDate), new Date(sprint.endDate)),
  });

  return (
    <>
      <div className={className}>
        <Card className="flex h-full flex-col gap-4">
          {/* On a phone the answer buttons take a row of their own under the title, so the title keeps its line. */}
          <div className="flex flex-wrap items-start gap-3 sm:flex-nowrap">
            <div className="min-w-0 flex-1">
              <p className="text-base font-semibold text-foreground">{t('productDashboard.latestReportTitle')}</p>
              <p className="text-xs text-muted-foreground">{which}</p>
            </div>
            {awaiting && (
              <div className="order-last flex w-full gap-2 sm:order-none sm:w-auto sm:shrink-0 print:hidden">
                <Button variant="outline" size="sm" onClick={() => setDeciding('dispute')}>
                  {t('actions.dispute')}
                </Button>
                <Button size="sm" onClick={() => setDeciding('approve')}>
                  {t('approve')}
                </Button>
              </div>
            )}
            <div className="flex shrink-0 items-center gap-2">
              {/* On paper the buttons go, so the chip says what they would have: that it waits on approval. */}
              <span
                className={cx(
                  'shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium',
                  REPORT_STATE_CHIP[chip],
                  awaiting && 'hidden print:inline',
                )}
              >
                {t(`productDashboard.latestReportState.${chip}`)}
              </span>
              {/* The report is a page of its own, so it swipes in the way the Sprints tab opens one. */}
              <CardArrow to={to} label={t('productDashboard.latestReportOpen')} swipe />
            </div>
          </div>

          {work && (
            <div className="flex flex-wrap gap-x-12 gap-y-3 max-sm:grid max-sm:grid-cols-3 max-sm:gap-3">
              {/* What was planned beside what it came to; the gap is said plainly, not coloured as a verdict. */}
              {budgeted !== null && (
                <div>
                  <p className="text-xl font-semibold tabular-nums text-foreground max-sm:text-lg">
                    {wholeEuros.format(budgeted)}
                  </p>
                  <p className="text-xs text-muted-foreground max-sm:text-[0.6875rem] max-sm:leading-tight">
                    {t('productDashboard.latestReportBudgeted')}
                  </p>
                </div>
              )}
              <div>
                <p className="text-xl font-semibold tabular-nums text-foreground max-sm:text-lg">
                  {costUnpriced ? '—' : wholeEuros.format(cost)}
                </p>
                <p className="text-xs text-muted-foreground max-sm:text-[0.6875rem] max-sm:leading-tight">
                  {budgeted === null || Math.round(cost - budgeted) === 0
                    ? t('productDashboard.latestReportCost')
                    : t(cost > budgeted ? 'productDashboard.latestReportOver' : 'productDashboard.latestReportUnder', {
                        amount: wholeEuros.format(Math.abs(cost - budgeted)),
                      })}
                </p>
              </div>
              <div>
                <p className="text-xl font-semibold tabular-nums text-foreground max-sm:text-lg">
                  {t('productDashboard.chapterHours', { count: Math.round(work.hours) })}
                </p>
                <p className="text-xs text-muted-foreground max-sm:text-[0.6875rem] max-sm:leading-tight">
                  {t('productDashboard.latestReportHours')}
                </p>
              </div>
            </div>
          )}

          {/* Its own box, so the PM's words read as the report's summary rather than a caption. */}
          <div className="flex-1 rounded-xl bg-muted/50 px-4 py-3">
            <p className="mb-1 text-xs font-medium text-muted-foreground">
              {t('productDashboard.latestReportSummary')}
            </p>
            <p className="line-clamp-4 text-sm text-foreground">
              {report.summary ?? (
                <span className="text-muted-foreground">{t('productDashboard.latestReportNoSummary')}</span>
              )}
            </p>
          </div>
        </Card>
      </div>
      {deciding === 'approve' && (
        <ApprovalPopup
          approveSprintProduct={(values: ApproveSprintFormValues) => approve(values, done)}
          sprintProduct={{ ...sprintProduct?.items[0] }}
          productId={productId}
          sprintId={sprint.id}
          onClose={() => setDeciding(null)}
          isLoading={sprintProductLoading}
          isApproving={isApproving}
        />
      )}
      {deciding === 'dispute' && (
        <DisputePopup
          disputeSprintProduct={(values: DisputeSprintFormValues) => dispute(values, done)}
          sprintProduct={{ ...sprintProduct?.items[0] }}
          productId={productId}
          sprintId={sprint.id}
          onClose={() => setDeciding(null)}
          isLoading={sprintProductLoading}
          isDisputing={isDisputing}
        />
      )}
    </>
  );
};

// Open incidents, and how many were resolved this year; only for a product that has had any, and while the flag is on.
const useIncidentsTile = (year: string): Tile | null => {
  const { t } = useTranslation();
  const { productId } = useParams<{ productId: string }>();
  const incidents = useProductIncidents(isProductIncidentsEnabled() ? productId : undefined).data?.items ?? [];
  if (!incidents.length) return null;
  const open = incidents.filter(incident => !incident.resolvedAt && !incident.closedAt).length;
  const resolved = incidents.filter(incident => (incident.resolvedAt ?? '').startsWith(year)).length;
  return {
    label: t('productDashboard.incidentsTile'),
    value: t('productDashboard.incidentsOpen', { count: open }),
    sub: t('productDashboard.incidentsResolved', { count: resolved, year }),
  };
};

const DONUT_SEGMENTS = [
  { key: 'current', labelKey: 'depCurrent', field: 'current', status: 'good' },
  { key: 'behind', labelKey: 'depBehind', field: 'behind', status: 'watch' },
  { key: 'unsupported', labelKey: 'depUnsupported', field: 'unsupported', status: 'risk' },
] as const;

/** The packages as a ring of their three states: the Health tab's at full size, the overview's small. */
const DependencyDonut = ({ dependencies, size }: { dependencies: DependenciesMetric; size: number }): JSX.Element => {
  const r = 42;
  const circ = 2 * Math.PI * r;
  // A white gap between states: flush butt ends met head on at the top and read as a seam.
  const arcs = ringArcs(
    DONUT_SEGMENTS.map(seg => dependencies[seg.field]),
    circ,
  );
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" className="-rotate-90">
      {/* Track ring in the theme border token so it adapts to dark mode. */}
      <circle cx={50} cy={50} r={r} fill="none" strokeWidth={9} style={{ stroke: 'var(--border)' }} />
      {DONUT_SEGMENTS.map((seg, index) => {
        const arc = arcs[index];
        if (!arc) return null;
        return (
          <circle
            key={seg.key}
            cx={50}
            cy={50}
            r={r}
            fill="none"
            strokeWidth={9}
            style={{
              stroke: STATUS_META[seg.status].stroke,
              strokeDasharray: `${arc.length} ${circ}`,
              strokeDashoffset: -arc.start,
            }}
          />
        );
      })}
    </svg>
  );
};

/** The ring small, with its count inside and the three states beside it. */
const MiniDependencyRing = ({ dependencies }: { dependencies: DependenciesMetric }): JSX.Element => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-4 max-sm:gap-2">
      <div className="relative inline-flex shrink-0 items-center justify-center max-sm:[&_svg]:size-12">
        <DependencyDonut dependencies={dependencies} size={66} />
        <span className="pointer-events-none absolute text-[13px] font-bold tabular-nums text-foreground max-sm:text-[11px]">
          {dependencies.packagesUsed}
        </span>
      </div>
      <div className="flex min-w-0 flex-col gap-0.5">
        {DONUT_SEGMENTS.map(seg => (
          <span
            key={seg.key}
            className="flex items-center gap-1.5 text-[13px] text-muted-foreground max-sm:gap-1 max-sm:text-[11px]"
          >
            <span
              className="size-2.5 shrink-0 rounded-full max-sm:size-2"
              style={{ background: STATUS_META[seg.status].stroke }}
            />
            <span className="font-semibold tabular-nums text-foreground">{dependencies[seg.field]}</span>
            <span className="truncate">{t(`productDashboard.${seg.labelKey}`).toLowerCase()}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

type HealthPreviewItem = { name: string; status: HealthStatus; reading: string; visual: ReactNode };

// The Health tab in miniature: each connected check's verdict colour and the shape of its reading.
const useHealthPreview = (): HealthPreviewItem[] => {
  const { t } = useTranslation();
  const { productId } = useParams<{ productId: string }>();
  const security = useProductSecurity(productId).data;
  const dependencies = useProductDependencies(productId).data;
  const responseTime = useProductResponseTime(productId).data;
  const crashFree = useProductCrashFree(productId).data;
  const items: HealthPreviewItem[] = [];

  const sec = security && aikido.getSecurityFrom(security);
  if (sec) {
    // One figure, like crash-free: the tab has the split by severity.
    items.push({
      name: t('productDashboard.security'),
      status: sec.status,
      reading: '',
      visual: (
        <div className="flex items-baseline gap-2">
          <p className={cx('text-2xl font-semibold tabular-nums max-sm:text-xl', STATUS_META[sec.status].text)}>
            {sec.total || t('productDashboard.allClear')}
          </p>
          {sec.total > 0 && (
            <p className="text-[13px] text-muted-foreground max-sm:text-[11px]">{t('productDashboard.openIssues')}</p>
          )}
        </div>
      ),
    });
  }
  const deps = dependencies && github.getDependenciesFrom(dependencies);
  if (deps) {
    items.push({
      name: t('productDashboard.dependencies'),
      status: deps.status,
      // The ring's legend already says how many are behind.
      reading: '',
      visual: <MiniDependencyRing dependencies={deps} />,
    });
  }
  const speed = responseTime && sentry.getResponseTimeFrom(responseTime);
  if (speed) {
    items.push({
      name: t('productDashboard.responseTime'),
      status: speed.status,
      reading: speed.value,
      // The last month: a daily series, so thirty points.
      visual: (
        <ResponseSparkline
          series={speed.series.slice(-30)}
          fastBelow={RESPONSE_FAST_BELOW_MS}
          slowAtOrAbove={RESPONSE_SLOW_AT_OR_ABOVE_MS}
          className="h-10 w-full"
        />
      ),
    });
  }
  const crashes = crashFree && sentry.getCrashFreeSessionsFrom(crashFree);
  if (crashes) {
    // A share this close to 100% reads better as the figure itself than as a bar that always looks full.
    items.push({
      name: t('productDashboard.crashFree'),
      status: crashes.status,
      reading: '',
      visual: (
        <p className={cx('text-2xl font-semibold tabular-nums max-sm:text-xl', STATUS_META[crashes.status].text)}>
          {crashes.value}
        </p>
      ),
    });
  }
  // The short ones share the top row and the two taller pictures the bottom, so each row is one height.
  const order = [
    t('productDashboard.security'),
    t('productDashboard.crashFree'),
    t('productDashboard.dependencies'),
    t('productDashboard.responseTime'),
  ];
  return items.sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name));
};

// Two columns wide, so each check gets room for its shape; the tab holds the rest.
const HealthPreview = ({ items, className }: { items: HealthPreviewItem[]; className?: string }): JSX.Element => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const healthPath = `${pathname.replace(/\/$/, '')}/monitoring`;
  return (
    <Card className={cx('flex h-full flex-col gap-3', className)}>
      <div className="flex items-start justify-between gap-3">
        <p className="text-base font-semibold text-foreground">{t('productDashboard.healthTile')}</p>
        {/* One way in for the whole card: the checks inside are read, not clicked. */}
        {/* No scroll target: the tab opens where the reader already is, which is what was asked for. */}
        <CardArrow to={healthPath} label={t('productDashboard.openTab', { tab: t('tabs.monitoring') })} />
      </div>
      {/* Two to a row on a phone too, each check shrunk to fit its half. */}
      <div className="grid flex-1 grid-cols-2 gap-3 max-sm:gap-2">
        {items.map(item => (
          <div
            key={item.name}
            className="flex min-w-0 flex-col gap-2 rounded-xl bg-muted/50 px-4 py-3 max-sm:gap-1.5 max-sm:px-3 max-sm:py-2.5"
          >
            <div className="flex items-center gap-2 text-[15px] max-sm:gap-1.5 max-sm:text-[13px]">
              <span
                aria-hidden
                className="size-3 shrink-0 rounded-full max-sm:size-2.5"
                style={{ backgroundColor: STATUS_META[item.status].stroke }}
              />
              <span className="min-w-0 flex-1 truncate text-foreground">{item.name}</span>
              {item.reading && (
                <span
                  className={cx(
                    'shrink-0 text-[13px] font-medium tabular-nums max-sm:text-[11px]',
                    STATUS_META[item.status].text,
                  )}
                >
                  {item.reading}
                </span>
              )}
            </div>
            {/* Centred in the row's height, so a short picture lines up with a taller one beside it. */}
            <div className="flex flex-1 items-center">{item.visual}</div>
          </div>
        ))}
      </div>
    </Card>
  );
};

// Label, then the figure and what it covers: as tall as that, with no stretching.
// A card from `sm` up; on a phone the three share one strip (OverviewFigures), so each is only a column of it.
const OverviewStat = ({ tile }: { tile: Tile }): JSX.Element => (
  <div className="flex min-w-0 break-inside-avoid flex-col gap-3 max-sm:gap-1 max-sm:p-3 sm:rounded-2xl sm:border sm:border-border sm:bg-card sm:p-4 sm:shadow-sm md:p-6">
    <p className="text-sm font-medium text-muted-foreground max-sm:min-h-[2lh] max-sm:text-xs">{tile.label}</p>
    <div>
      <p className="text-3xl font-bold tabular-nums text-foreground max-sm:text-lg">{tile.value}</p>
      <p className="mt-1 text-xs text-muted-foreground max-sm:text-[0.6875rem] max-sm:leading-tight">{tile.sub}</p>
    </div>
  </div>
);

// One figure from each area of the product, the first thing on the page.
const OverviewFigures = ({ spend }: { spend: YearSpend }): JSX.Element => {
  const { t } = useTranslation();
  const team: Tile = {
    label: t('productDashboard.teamTile'),
    // A headcount, not money: it waits only on the read, never on whether an hour could be priced.
    value: pendingRead(t, spend) ?? t('productDashboard.teamTileValue', { count: spend.activePeople }),
    sub: spend.failed ? t('cardReadFailed') : t('productDashboard.teamTileSub', { hours: ACTIVE_HOURS }),
  };
  return (
    <div className="mt-2 mb-4 grid grid-cols-3 gap-4 max-sm:-mx-4 max-sm:gap-0 max-sm:divide-x max-sm:divide-border max-sm:border-y max-sm:border-border max-sm:bg-card">
      {[...spendStats(t, spend), team].map(tile => (
        <OverviewStat key={tile.label} tile={tile} />
      ))}
    </div>
  );
};

// The latest report and the product's health, under the spend chart.
const OverviewDetail = ({ spend }: { spend: YearSpend }): JSX.Element | null => {
  const report = useLatestReport();
  const health = useHealthPreview();
  const incidents = useIncidentsTile(spend.year);
  if (!report && !incidents && !health.length) return null;
  return (
    <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:auto-rows-[18rem] lg:grid-cols-4">
      {report && (
        <LatestReportTile report={report} spend={spend} className={reportTileSpan(!!incidents, health.length > 0)} />
      )}
      {incidents && <OverviewStat tile={incidents} />}
      {health.length > 0 && <HealthPreview items={health} className="sm:col-span-2" />}
    </div>
  );
};

// A finished product drops delivery, but keeps its health and incidents: the PRD's "health alone".
const FinishedOverview = ({ year }: { year: string }): JSX.Element | null => {
  const health = useHealthPreview();
  const incidents = useIncidentsTile(year);
  if (!incidents && !health.length) return null;
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:auto-rows-[18rem] lg:grid-cols-4">
      {incidents && <OverviewStat tile={incidents} />}
      {health.length > 0 && <HealthPreview items={health} className="sm:col-span-2" />}
    </div>
  );
};

const ProgressSection = ({ actions, spend }: { actions?: ReactNode; spend?: YearSpend }): JSX.Element => {
  const { t } = useTranslation();
  if (spend) {
    return (
      <section>
        <SectionHeading action={actions}>{t('productDashboard.overviewSection')}</SectionHeading>
        <OverviewFigures spend={spend} />
        <SpendReport spend={spend} />
        <OverviewDetail spend={spend} />
      </section>
    );
  }
  return <HoursProgress actions={actions} />;
};

// The Aikido per-check breakdown, listed in full: the Health tab is where the detail is read.
const AikidoChecks = ({ checks }: { checks: { name: string; issues: number }[] }): JSX.Element => {
  const { t } = useTranslation();
  return (
    <div className="mt-5 border-t border-border pt-4">
      <p className="text-sm font-medium text-foreground">{t('productDashboard.aikidoChecks')}</p>
      <div className="mt-2">
        {checks.map(check => {
          const hasIssues = check.issues > 0;
          const meta = STATUS_META[countStatus(check.issues)];
          const Icon = hasIssues ? TriangleAlert : CircleCheck;
          return (
            <div
              key={check.name}
              className="flex items-center justify-between gap-3 border-b border-border py-2.5 last:border-b-0"
            >
              <span className="flex min-w-0 items-center gap-2">
                <Icon className={cx('size-4 shrink-0', meta.text)} />
                <span className="truncate text-sm text-foreground">{check.name}</span>
              </span>
              <span className={cx('shrink-0 text-sm font-semibold tabular-nums', meta.text)}>
                {hasIssues ? t('productDashboard.nIssues', { count: check.issues }) : t('productDashboard.clear')}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Response time card: the current value + verdict, and a line chart of the daily p75 over Sentry's
// retention window. The Fast / Moderate / Slow colour zones behind the line carry the "is this good?"
// meaning so a client can read a rising line as "getting slower" without knowing the benchmark.
// `responseTime === null` means Sentry has no data for this product. That renders as an explicit
// unknown rather than a zero, because a client reads "0 ms" as perfect speed.
const ResponseTimeCard = ({
  responseTime,
  isLoading,
  windowDays,
}: {
  responseTime: ResponseTimeMetric | null;
  isLoading?: boolean;
  windowDays?: number;
}): JSX.Element => {
  const { t } = useTranslation();
  const meta = responseTime ? STATUS_META[responseTime.status] : null;
  return (
    <HealthCard
      title={
        <>
          {t('productDashboard.responseTime')}{' '}
          <span className="text-sm font-medium text-muted-foreground">
            ({t('productDashboard.responseTimePercentile')})
          </span>
        </>
      }
      threshold={responseTimeThreshold(t)}
      className="flex flex-col"
    >
      {!responseTime || !meta ? (
        <div className="flex flex-1 flex-col justify-center py-6">
          <p className="text-sm text-muted-foreground">
            {isLoading ? t('productDashboard.responseTimeLoading') : t('productDashboard.responseTimeNoData')}
          </p>
        </div>
      ) : (
        <>
          {/* The window takes the slot the verdict had, beside the number, so it can only be read as
              qualifying that number: in the card title it read as though each dot on the chart spanned 90
              days, and on its own line above the chart it read as a caption for the chart. */}
          <p className="flex items-baseline gap-2">
            <span className={cx('text-2xl font-bold', meta.text)}>{responseTime.value}</span>
            <span className="text-sm font-semibold text-muted-foreground">
              {t('productDashboard.responseTimeWindowLabel', { days: windowDays ?? 90 })}
            </span>
          </p>
          <ResponseTrendChart
            series={responseTime.series}
            fastBelow={RESPONSE_FAST_BELOW_MS}
            slowAtOrAbove={RESPONSE_SLOW_AT_OR_ABOVE_MS}
            formatValue={formatMsTick}
            className="mt-4 min-h-0 flex-1"
          />
          <p className="mt-4 text-xs text-muted-foreground">{t('productDashboard.responseTimeCaption')}</p>
        </>
      )}
    </HealthCard>
  );
};

// Crash-free sessions: the rate and the sessions behind it, then the same daily line response time has.
const CrashFreeCard = ({
  crashFree,
  isLoading,
}: {
  crashFree: CrashFreeMetric | null;
  isLoading?: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  const pctGreen = THRESHOLDS.crashFreeSessionsPctGreenAtOrAbove;
  return (
    <HealthCard
      title={t('productDashboard.crashFree')}
      threshold={t('productDashboard.crashFreeThreshold', { pct: pctGreen })}
      className="flex flex-col"
    >
      {!crashFree ? (
        <div className="flex flex-1 flex-col justify-center py-6">
          <p className="text-sm text-muted-foreground">
            {isLoading ? t('productDashboard.crashFreeLoading') : t('productDashboard.crashFreeNoData')}
          </p>
        </div>
      ) : (
        <>
          <p className="flex items-baseline gap-2">
            <span className={cx('text-2xl font-bold', STATUS_META[crashFree.status].text)}>{crashFree.value}</span>
            {crashFree.detail && (
              <span className="text-sm font-semibold text-muted-foreground">{crashFree.detail}</span>
            )}
          </p>
          {crashFree.series.filter(point => !point.gap).length > 1 && (
            <CrashFreeTrendChart series={crashFree.series} greenAtOrAbove={pctGreen} className="mt-4 min-h-0 flex-1" />
          )}
          <p className="mt-4 text-xs text-muted-foreground">{t('productDashboard.crashFreeCaption')}</p>
        </>
      )}
    </HealthCard>
  );
};

// A scan date reads as reassurance, so it is shown as a plain date rather than an ISO timestamp.
// Rendered in UTC: an early-morning scan otherwise shows the previous day to a viewer west of UTC, so
// the same scan would carry a different date per reader of the same client dashboard.
const formatScanDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

// `security === null` means Aikido does not scan this product. It renders as an explicit unknown: a
// zero would read to a client as a clean bill of health for something nobody has checked.
const SecurityCard = ({
  security,
  excludedArchived,
  isLoading,
  loadingLabel,
  noDataLabel,
}: {
  security: SecurityMetric | null;
  excludedArchived?: string[];
  isLoading?: boolean;
  loadingLabel?: string;
  noDataLabel?: string;
}): JSX.Element => {
  const { t } = useTranslation();

  if (!security) {
    return (
      <HealthCard title={t('productDashboard.security')} threshold={securityThreshold(t)}>
        <p className="text-sm text-muted-foreground">{isLoading ? loadingLabel : noDataLabel}</p>
        <ArchivedNote repositories={excludedArchived} />
      </HealthCard>
    );
  }

  const meta = STATUS_META[security.status];
  // The count sits beside the title; the severity split is on the list's filter chips.
  const count = (
    <p className="text-2xl font-bold leading-tight">
      <span className={meta.text}>{security.total}</span>{' '}
      <span className="text-base font-medium text-muted-foreground">{t('productDashboard.openIssues')}</span>
    </p>
  );
  return (
    <HealthCard title={t('productDashboard.security')} threshold={securityThreshold(t)} aside={count}>
      {/* With nothing open, the checks come back clean one by one, which a client can tell apart from unscanned. */}
      {security.issues.length > 0 ? (
        <SecurityIssues issues={security.issues} />
      ) : (
        <>
          <p className={cx('text-sm font-medium', meta.text)}>{t('productDashboard.noVulnerabilities')}</p>
          {security.checks.length > 0 && <AikidoChecks checks={security.checks} />}
        </>
      )}
      <p className="mt-4 text-xs text-muted-foreground">
        {t('productDashboard.securityCaption')}
        {security.lastScannedAt &&
          ` ${t('productDashboard.securityLastScanned', { date: formatScanDate(security.lastScannedAt) })}`}
      </p>
      <ArchivedNote repositories={excludedArchived} />
    </HealthCard>
  );
};

const DependencyRing = ({ dependencies }: { dependencies: DependenciesMetric }): JSX.Element => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col items-center justify-center gap-6 sm:flex-row sm:gap-8 lg:flex-col lg:gap-5">
      <div className="relative inline-flex shrink-0 items-center justify-center">
        <DependencyDonut dependencies={dependencies} size={150} />
        <div className="pointer-events-none absolute flex flex-col items-center leading-none">
          <span className="text-3xl font-bold tabular-nums text-foreground">{dependencies.packagesUsed}</span>
          <span className="mt-1 text-[11px] text-muted-foreground">{t('productDashboard.packages')}</span>
        </div>
      </div>

      {/* Legend — a colour dot then "<count> <label>" per bucket. */}
      <div className="flex flex-col gap-2.5">
        {DONUT_SEGMENTS.map(seg => (
          <div key={seg.key} className="flex items-center gap-2.5">
            <span className="size-2.5 shrink-0 rounded-full" style={{ background: STATUS_META[seg.status].stroke }} />
            <span className="text-sm text-foreground">
              <span className="font-semibold tabular-nums">{dependencies[seg.field]}</span>{' '}
              {t(`productDashboard.${seg.labelKey}`).toLowerCase()}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// One card: the package-freshness ring with its legend under it on the left; past a divider, every graded
// package, searchable and filterable by bucket, worst first. Runtime and framework end-of-life live in
// the (internal) detail view, not here.
// `dependencies === null` means no inventory covers this product. It renders as an explicit unknown: a
// donut showing everything green would read as a clean bill of health for a product nobody has checked.
// The caller picks the wording, because an unreadable source is a different unknown from an unchecked one.
const DependenciesCard = ({
  dependencies,
  excludedArchived,
  isLoading,
  loadingLabel,
  noDataLabel,
}: {
  dependencies: DependenciesMetric | null;
  excludedArchived?: string[];
  isLoading?: boolean;
  loadingLabel?: string;
  noDataLabel?: string;
}): JSX.Element => {
  const { t } = useTranslation();

  if (!dependencies) {
    return (
      <HealthCard title={t('productDashboard.dependencies')} threshold={dependenciesThreshold(t)}>
        <p className="text-sm text-muted-foreground">{isLoading ? loadingLabel : noDataLabel}</p>
        <ArchivedNote repositories={excludedArchived} />
      </HealthCard>
    );
  }

  return (
    <HealthCard title={t('productDashboard.dependencies')} threshold={dependenciesThreshold(t)}>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_2fr] lg:items-center lg:gap-0">
        <div className="flex items-center justify-center lg:pr-8">
          <DependencyRing dependencies={dependencies} />
        </div>
        <div className="min-w-0 border-t border-border pt-6 lg:border-l lg:border-t-0 lg:pl-8 lg:pt-0">
          <DependencyList packages={dependencies.packages} />
        </div>
      </div>
      <DependenciesCaption
        checkedOn={dependencies.checkedAt && formatScanDate(dependencies.checkedAt)}
        uncheckedCount={dependencies.uncheckedCount}
      />
      <ArchivedNote repositories={excludedArchived} />
    </HealthCard>
  );
};

export const HealthSection = ({ finished }: { finished: boolean }): JSX.Element => {
  const { t } = useTranslation();
  const { productId } = useParams<{ productId: string }>();
  // Every card in this section is real per-product data from the API.
  const { data: responseTime, isLoading: isResponseTimeLoading } = useProductResponseTime(productId);
  const realResponseTime = responseTime ? sentry.getResponseTimeFrom(responseTime) : null;
  const { data: crashFree, isLoading: isCrashFreeLoading } = useProductCrashFree(productId);
  const realCrashFree = crashFree ? sentry.getCrashFreeSessionsFrom(crashFree) : null;
  const { data: dependencies, isLoading: isDependenciesLoading } = useProductDependencies(productId);
  const realDependencies = dependencies ? github.getDependenciesFrom(dependencies) : null;
  const { data: security, isLoading: isSecurityLoading } = useProductSecurity(productId);
  const realSecurity = security ? aikido.getSecurityFrom(security) : null;
  return (
    <>
      {/* Group 1 — code & risk that accrues over time: Security then Dependencies, each a full-width
          card stacked one per row. */}
      <section>
        <SectionHeading>{t('productDashboard.healthSection')}</SectionHeading>
        <p className="mb-4 -mt-1 max-w-3xl text-sm text-muted-foreground">
          {finished ? t('productDashboard.healthIntroFinished') : t('productDashboard.healthIntroActive')}
        </p>

        <div className="grid grid-cols-1 gap-4">
          <SecurityCard
            security={realSecurity}
            excludedArchived={security?.excludedArchived}
            isLoading={isSecurityLoading}
            noDataLabel={t(aikido.noDataKeyFor(security))}
            loadingLabel={t('productDashboard.securityLoading')}
          />
          <DependenciesCard
            dependencies={realDependencies}
            excludedArchived={dependencies?.excludedArchived}
            isLoading={isDependenciesLoading}
            noDataLabel={t(github.noDataKeyFor(dependencies), { count: dependencies?.uncheckedCount })}
            loadingLabel={t('productDashboard.dependenciesLoading')}
          />
        </div>
      </section>

      {/* Group 2 — how the live app is behaving for real visitors. */}
      <section>
        {/* Whole group is Sentry-sourced, so attribute it next to the label (like "via Aikido"). */}
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('productDashboard.liveAppPerformance')}{' '}
          <span className="font-normal normal-case tracking-normal">(via Sentry)</span>
        </p>
        <p className="mb-4 -mt-1 max-w-3xl text-sm text-muted-foreground">
          {t('productDashboard.liveAppPerformanceCaption')}
        </p>
        {/* Both carry a daily line over the same window, so the two cards read as a pair. */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <ResponseTimeCard
            responseTime={realResponseTime}
            isLoading={isResponseTimeLoading}
            windowDays={responseTime?.windowDays}
          />
          <CrashFreeCard crashFree={realCrashFree} isLoading={isCrashFreeLoading} />
        </div>
      </section>
    </>
  );
};

/**
 * Active and support products lead with Progress; Health has its own tab. An archived product drops the
 * money altogether, Progress and the budget and spend below it, and keeps its health and incidents, since
 * there is no ongoing work to report.
 *
 * The prototype closes with an Accessibility and a GDPR card. Both are computed from a fixture rather
 * than measured, and a compliance verdict is the last thing to show a client on trust alone, so neither
 * ships until a source exists.
 */
const ProductDashboard = ({
  finished,
  actions,
  spend,
  children,
}: {
  finished?: boolean;
  /** On the first section's line, or on a line of their own when there is no Progress. */
  actions?: ReactNode;
  /** The year's spend, priced; without it the section reads in hours. */
  spend?: YearSpend;
  /** The year's budget and spend, after the delivery it pays for; not for a finished product. */
  children?: ReactNode;
}): JSX.Element => {
  const isFinished = Boolean(finished);
  const showsProgress = !isFinished;
  return (
    <div className="flex flex-col gap-10">
      {showsProgress ? (
        <ProgressSection actions={actions} spend={spend} />
      ) : (
        actions && <div className="flex justify-end print:hidden">{actions}</div>
      )}
      {isFinished && <FinishedOverview year={spend?.year ?? String(new Date().getFullYear())} />}
      {!isFinished && children}
    </div>
  );
};

export default ProductDashboard;
